import { Injectable, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AhaSettings, configurationSelectors, settingsSelectors, State, User } from '@wam/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { authenticationSelectors } from '@wam/authentication';
import { filter, map, take, tap } from 'rxjs/operators';
import { ConfigurationDataService } from '@app/configuration/configuration-data.service';
import { saveUserConfiguration } from '@app/configuration/state/configuration.actions';
import { isReportsApp } from '@app/reports-app/reports-app.util';

declare global {
  interface Window {
    aha?: (...args: unknown[]) => unknown;
  }
}

export type AhaPollConfig = {
  ahaPollMaxSeen: number;
  ahaPollSeenFrequencyDays: number;
};

export type AhaPollHistory = {
  pollId: string;
  lastSeenTimestamp: number;
  seenCount: number;
};

export type AhaPollHistoryData = {
  polls: AhaPollHistory[];
  lastSeenTimestamp: number;
};

const AHA_POLL_CONFIG_KEY: string = 'AHA_POLL_CONFIG';

export enum AhaPoll {
  STUDENT_PLAYLIST = 'AHA_STUDENT_PLAYLIST_POLL_ID',
  CLASS_PLAYLIST = 'AHA_CLASS_PLAYLIST_POLL_ID',
  AHA_DISTRICT_ADMIN_FEEDBACK_POLL = 'AHA_DISTRICT_ADMIN_FEEDBACK_POLL',
  AHA_SCHOOL_ADMIN_FEEDBACK_POLL = 'AHA_SCHOOL_ADMIN_FEEDBACK_POLL',
  AHA_TEACHER_FEEDBACK_POLL = 'AHA_TEACHER_FEEDBACK_POLL',
}

@Injectable({
  providedIn: 'root',
})
export class AhaService {
  private readonly store = inject<Store<State>>(Store);
  private readonly configurationDataService = inject(ConfigurationDataService);

  private readonly ahaSettings$: Observable<AhaSettings> = this.store.pipe(
    select(settingsSelectors.getAhaSettings),
  );

  private readonly currentUser$: Observable<User> = this.store.pipe(
    select(authenticationSelectors.getCurrentUser),
  );

  private ahaPollConfig: AhaPollConfig;

  load(): Observable<boolean> {
    // The feedback widget has no place in printing views nor in the standalone reports app (whose
    // users - e.g. `ext-parent` - are not Waterford staff to be polled for feedback).
    if (this.isPrintingContext() || isReportsApp()) {
      return of(true);
    }

    return combineLatest([this.ahaSettings$, this.currentUser$]).pipe(
      take(1),
      map(([ahaSettings, { sub, name, email }]) => {
        // `feedback.js` itself crashes (`reading 'account'`) when initialized without an account,
        // so never inject it unless the settings are complete.
        if (email && ahaSettings?.account && ahaSettings?.applicationId) {
          this.loadAhaScript();
          this.initializeAha(ahaSettings.account, ahaSettings.applicationId, sub, name, email);
        }
      }),
      map(() => true),
    );
  }

  /**
   * `globalThis.aha` only exists once `load()` has injected the widget script - which it skips on
   * printing routes, for users without an email, etc.
   */
  private isWidgetAvailable(): boolean {
    return typeof globalThis.aha === 'function';
  }

  private isPrintingContext(): boolean {
    return globalThis.location?.pathname?.includes('-printing') ?? false;
  }

  private loadAhaScript() {
    (function (w, d, s, o, f, js, fjs) {
      w['aha-widget'] = o;
      w[o] =
        w[o] ||
        function () {
          (w[o].q = w[o].q || []).push(arguments);
        };
      js = d.createElement(s);
      fjs = d.getElementsByTagName(s)[0];
      js.id = o;
      js.src = f;
      js.async = 1;
      fjs.parentNode.insertBefore(js, fjs);
    })(globalThis, document, 'script', 'aha', 'https://cdn.aha.io:443/assets/feedback/feedback.js');
  }

  private initializeAha(account, applicationId, userId, name, email) {
    globalThis.aha('initialize', {
      account,
      applicationId,
      user: {
        id: userId,
        name,
        email,
      },
    });
  }

  getConfig(): Observable<AhaPollConfig> {
    if (this.ahaPollConfig) {
      return of(this.ahaPollConfig);
    }

    return this.configurationDataService.getGlobal(AHA_POLL_CONFIG_KEY).pipe(
      map((data) => {
        return data ? (JSON.parse(`${data.value}`) as AhaPollConfig) : null;
      }),
      tap((config) => (this.ahaPollConfig = config)),
    );
  }

  getPollHistory(): Observable<AhaPollHistoryData> {
    return this.store.pipe(
      select(configurationSelectors.getUserConfiguration<AhaPollHistoryData>('userAhaPollHistory')),
    );
  }

  updatePollHistory(pollHistory: AhaPollHistoryData): void {
    this.store.dispatch(
      saveUserConfiguration({
        key: 'userAhaPollHistory',
        value: pollHistory,
      }),
    );
  }

  showPoll(key: AhaPoll): void {
    // `globalThis.aha` only exists once `load()` has injected the widget script. Bail out early when
    // it hasn't (e.g. standalone reports app, printing routes) - this also avoids issuing the
    // `AHA_POLL_CONFIG` configuration request for nothing.
    if (!this.isWidgetAvailable()) {
      return;
    }

    combineLatest([this.getPollId(key), this.getConfig(), this.getPollHistory(), this.currentUser$])
      .pipe(
        filter(([pollId, config, , user]) => !!pollId && !!config && !!user.email),
        take(1),
      )
      .subscribe(([pollId, config, pollHistory]) => {
        const currentTimestamp = Date.now();
        const existingPoll = pollHistory?.polls?.find((p) => p.pollId === pollId);

        const canShowPoll = this.canShowPoll(
          existingPoll,
          pollHistory?.lastSeenTimestamp,
          config,
          currentTimestamp,
        );

        if (canShowPoll && this.isWidgetAvailable()) {
          globalThis.aha('open', pollId);

          this.updatePollHistoryForPoll(pollHistory, pollId, currentTimestamp);
        }
      });
  }

  private getPollId(key: AhaPoll): Observable<string> {
    return this.store.pipe(
      select(configurationSelectors.getConfigurationForKey(key)),
    ) as Observable<string>;
  }

  private canShowPoll(
    existingPoll: AhaPollHistory | undefined,
    lastSeenTimestamp: number,
    config: AhaPollConfig,
    currentTimestamp: number,
  ): boolean {
    if (config.ahaPollMaxSeen === 0) {
      return false;
    }

    if (!existingPoll && !lastSeenTimestamp) {
      return true;
    }

    if (existingPoll && existingPoll.seenCount >= config.ahaPollMaxSeen) {
      return false;
    }

    const daysSinceLastSeen = (currentTimestamp - lastSeenTimestamp) / (1000 * 60 * 60 * 24);
    return daysSinceLastSeen >= config.ahaPollSeenFrequencyDays;
  }

  private updatePollHistoryForPoll(
    currentHistory: AhaPollHistoryData,
    pollKey: string,
    timestamp: number,
  ): void {
    const updatedHistory: AhaPollHistoryData = {
      polls: currentHistory ? [...currentHistory.polls] : [],
      lastSeenTimestamp: timestamp,
    };

    const existingPollIndex = updatedHistory.polls.findIndex((p) => p.pollId === pollKey);

    if (existingPollIndex >= 0) {
      updatedHistory.polls[existingPollIndex] = {
        ...updatedHistory.polls[existingPollIndex],
        lastSeenTimestamp: timestamp,
        seenCount: updatedHistory.polls[existingPollIndex].seenCount + 1,
      };
    } else {
      updatedHistory.polls.push({
        pollId: pollKey,
        lastSeenTimestamp: timestamp,
        seenCount: 1,
      });
    }

    this.updatePollHistory(updatedHistory);
  }
}
